import React from 'react';

import moment from 'moment';
import styled from 'styled-components';

import { AmazonLogo } from 'components/common/icons/Amazon';

import { useDataProviderAllowed } from './useDataProviderAllowed';
import { AdsProviderLogo } from '../../common/AdsProviderLogo';


export const TIMESTAMP_UI_DATE_FORMAT = 'M/DD/YY HH:mm [UTC]';

function adsMetricsFn(provider) {
  return [
    provider.campaignMetricsProcessingStatus.lastProcessedAt,
    provider.campaignMetricsProcessingStatus.lastErrorAt,
  ];
}

function adsClickConversionsFn(provider) {
  return [provider.clickConversionsUploadedAt, null];
}

function productSyncFn(provider) {
  return [provider.lastProcessedAt, provider.lastErrorAt];
}

function productBsrSyncFn(provider) {
  return [provider.bsrLastProcessedAt, null];
}

function productOrderSyncFn(provider) {
  return [provider.orderReportLastProcessedAt, null];
}

function getLatestAndError(providers, getFn, syncName = undefined) {
  const failedProviders = [];

  let latestSync = null;

  for (const provider of providers) {
    const [syncedAt, errorAt] = getFn(provider);

    if (errorAt) {
      failedProviders.push({ ...provider, _syncName: syncName });
    }

    if (!syncedAt || moment(syncedAt).isSameOrBefore(0)) {
      continue;
    }

    if (!latestSync || latestSync < syncedAt) {
      latestSync = syncedAt;
    }
  }

  return { latestSync, failedProviders };
}

function joinLatestAndError(resultsList) {
  return resultsList.reduce(
    (acc, { latestSync, failedProviders }) => {
      if (!acc.latestSync || latestSync > acc.latestSync) {
        acc.latestSync = latestSync;
      }

      acc.failedProviders.push(...failedProviders);

      return acc;
    },
    { latestSync: null, failedProviders: [] },
  );
}

export function getCombinedLatestAndError(adsProviders, productProviders) {
  const entries = [
    [adsProviders, adsMetricsFn],
    [adsProviders, adsClickConversionsFn],
    [productProviders, productSyncFn],
    [productProviders, productBsrSyncFn],
    [productProviders, productOrderSyncFn],
  ];

  // Loop through entries and ads providers and call getLatestAndError
  const results = entries.map(([providers, getFn]) => getLatestAndError(providers, getFn));

  // Aggregate results
  return joinLatestAndError(results);
}

function renderAdsProvider(name, providerType, frequency, providerDetails) {
  const { latestSync, failedProviders } = providerDetails;

  return (
    <li>
      <AdsProviderLogo style={{ height: 24, width: 24, marginTop: 2 }} providerType={`${providerType}`} />
      <div className="name">
        <div>{name}</div>
        <div className="details">
          <ul>
            {failedProviders.map((provider) => (
              <li>
                {provider._syncName && `${provider._syncName  } - `}
                {provider.externalAccountDescriptiveName}
              </li>
            ))}
          </ul>
          <div className="frequency"> {frequency}</div>
        </div>
      </div>

      <div className="status">
        {failedProviders.length > 0 ? (
          <span className="error">
            The most recent data refresh was not successful. Please reach out to support at help@carbon6.io&nbsp;
          </span>
        ) : (
          <>
            Last completed:&nbsp;
            <span>{latestSync ? moment(latestSync).utc().format(TIMESTAMP_UI_DATE_FORMAT) : 'N/A'}</span>
          </>
        )}
      </div>
    </li>
  );
}

function renderProductProvider(name, frequency, providerDetails) {
  const { latestSync, failedProviders } = providerDetails;

  return (
    <li>
      <AmazonLogo style={{ height: 24, width: 24, marginTop: 2 }} />
      <div className="name">
        <div>{name}</div>
        <div className="details">
          <ul className="failed-providers">
            {failedProviders.map((provider) => (
              <li>{provider.externalAccountDescriptiveName}</li>
            ))}
          </ul>
          <div className="frequency">{frequency}</div>
        </div>
      </div>

      <div className="status">
        {failedProviders.length > 0 ? (
          <span className="error">
            The most recent data refresh was not successful. Please reach out to support at help@carbon6.io&nbsp;
          </span>
        ) : (
          <>
            Last completed:&nbsp;
            <span>{latestSync ? moment(latestSync).utc().format(TIMESTAMP_UI_DATE_FORMAT) : 'N/A'}</span>
          </>
        )}
      </div>
    </li>
  );
}

/**
 * A hook used to open the data provider modal
 * @param {object} { account }
 * @returns {[func,boolean]} A function that opens the data provider modal and a `boolean` of if user is allowed.
 */
export const DataProviderModal = ({ account, children }) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const modalRef = React.useRef(null);

  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      setIsModalOpen(false);
    }
  };

  React.useEffect(() => {
    if (isModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isModalOpen]);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const MyModal = styled.div`
    position: fixed;

    background: rgba(250, 250, 250, 1);
    box-shadow: 0px 4px 14px 0px rgba(0, 0, 0, 0.35);

    width: 676px;
    max-height: calc(100vh - 124px);
    top: 62px;
    right: 62px;
    border-radius: 20px;
    padding-top: 16px;
    padding-right: 24px;
    padding-bottom: 24px;
    padding-left: 24px;
    z-index: 9999;
    overflow-y: auto;
    font-family: 'Open Sans';
    font-smooth: never;
    font-weight: 400;
    line-height: 100%;
    letter-spacing: 0%;
    font-feature-settings: normal !important;

    .heading {
      text-align: left;

      color: rgba(5, 17, 32, 1);

      margin: 0;

      font-weight: 600;
      font-size: 16px;
      vertical-align: middle;
      text-transform: capitalize;

      line-height: 22px;
    }
    .body {
      margin-top: 24px;
      padding: 0;
      padding-left: 6px;
      // padding-right: 9px;
    }

    ul.items {
      list-style-type: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 35px;

      > li {
        display: grid;
        gap: 12px;
        grid-template-columns: 24px 1fr 201px;
        font-size: 12px;

        .img {
          margin-top: 10px;
        }
        .name {
          flex-grow: 1;
          font-size: 14px;
        }
        .status {
          color: rgba(26, 33, 255, 1);
          .error {
            color: rgba(217, 63, 33, 1);
          }
        }
        .frequency {
          color: rgba(120, 120, 120, 1) !important;
          margin: 0;
          padding: 0;
        }
        .details {
          margin-top: 8px;
          ul {
            list-style-type: none;
            margin: 0;
            padding: 0;
            // margin-bottom: 10px;

            > li::before {
              content: '';
              display: inline-block;
              width: 0.18rem;
              height: 0.18rem;
              border-radius: 50%;
              background-color: black;
              margin-right: 0.4rem;
              margin-left: 8px;
            }
            > li {
              padding: 0;
              line-height: 1em;
              display: flex;
              align-items: center;
              overflow: hidden;
              margin-bottom: 10px;
            }
          }
          font-size: 12px;
        }
      }
    }
  `;

  const adsProviders = account.adsProviders.filter((p) => !p.generated);
  const facebookProviders = adsProviders.filter((p) => p.providerType === 'FACEBOOK');
  const googleProviders = adsProviders.filter((p) => p.providerType === 'GOOGLE');
  const tiktokProviders = adsProviders.filter((p) => p.providerType === 'TIKTOK');

  const { productProviders } = account;

  const adMetricFrequency = (
    <>
      Campaign Metrics updated once daily <br /> Click conversions updated twice daily
    </>
  ); // cron-import-campaigns-from-adsprovider, cron-upload-google-click-conversions

  const adMetricTikTokFrequency = 'Campaign Metrics updated once daily'; // cron-import-campaigns-from-adsprovider
  const prodMetricsFrequency = 'Updated 4 times a day'; // cron-productprovider-attribution
  const prodBSRFrequency = 'Updated once daily'; // cron-sync-bsr
  const prodOrdersFrequency = 'Updated once daily'; // cron-sync-spa-order-stats

  return (
    <>
      <div onMouseUp={toggleModal} ref={modalRef}>
        {children}
        {isModalOpen && (
          <MyModal style={{ display: 'unset' }}>
            <div className="heading">Data Synced</div>
            <div className="body">
              <ul className="items">
                {googleProviders.length > 0 && (
                  <>
                    {renderAdsProvider(
                      'Google Metrics',
                      'GOOGLE',
                      adMetricFrequency,
                      joinLatestAndError([
                        getLatestAndError(googleProviders, adsMetricsFn, 'Metrics'),
                        getLatestAndError(googleProviders, adsClickConversionsFn, 'Click Conversions'),
                      ]),
                    )}
                  </>
                )}
                {facebookProviders.length > 0 && (
                  <>
                    {renderAdsProvider(
                      'Facebook Metrics',
                      'FACEBOOK',
                      adMetricFrequency,
                      joinLatestAndError([
                        getLatestAndError(facebookProviders, adsMetricsFn, 'Metrics'),
                        getLatestAndError(facebookProviders, adsClickConversionsFn, 'Click Conversions'),
                      ]),
                    )}
                  </>
                )}
                {tiktokProviders.length > 0 && (
                  <>
                    {renderAdsProvider(
                      'TikTok Metrics',
                      'TIKTOK',
                      adMetricTikTokFrequency,
                      joinLatestAndError([
                        getLatestAndError(tiktokProviders, adsMetricsFn, 'Metrics'),
                        getLatestAndError(tiktokProviders, adsClickConversionsFn, 'Click Conversions'),
                      ]),
                    )}
                  </>
                )}

                {productProviders.length > 0 && (
                  <>
                    {renderProductProvider(
                      'Amazon Product Metrics',
                      prodMetricsFrequency,
                      getLatestAndError(productProviders, productSyncFn),
                    )}
                    {renderProductProvider(
                      'Best Selling Rank',
                      prodBSRFrequency,
                      getLatestAndError(productProviders, productBsrSyncFn),
                    )}
                    {renderProductProvider(
                      'Amazon Order Data',
                      prodOrdersFrequency,
                      getLatestAndError(productProviders, productOrderSyncFn),
                    )}
                  </>
                )}
              </ul>
            </div>
          </MyModal>
        )}
      </div>
    </>
  );
};
